import React from 'react';
import { Box, VStack, Text, Button } from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { surveyTheme as theme } from './surveyTheme';
import { t } from './translations';

/**
 * ErrorScreen - Friendly error states
 */
const ErrorScreen = ({ error, onRetry, lang = 'en' }) => {
  const getErrorInfo = () => {
    switch (error?.type) {
      case 'expired':
        return {
          title: t(lang, 'linkExpired'),
          message: error.message || t(lang, 'linkExpiredMessage'),
          showRetry: false,
        };
      case 'invalid':
        return {
          title: t(lang, 'invalidLink'),
          message: error.message || t(lang, 'invalidLinkMessage'),
          showRetry: false,
        };
      case 'rate_limit':
        return {
          title: t(lang, 'pleaseWait'),
          message: error.message || t(lang, 'pleaseWaitMessage'),
          showRetry: true,
        };
      case 'already_completed':
        return {
          title: t(lang, 'alreadyCompleted'),
          message: t(lang, 'alreadyCompletedMessage'),
          showRetry: false,
        };
      case 'network':
      default:
        return {
          title: t(lang, 'connectionError'),
          message: error?.message || t(lang, 'connectionErrorMessage'),
          showRetry: true,
        };
    }
  };

  const { title, message, showRetry } = getErrorInfo();

  return (
    <Box
      minH="100vh"
      bg={theme.colors.background}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={theme.spacing.screenPadding}
    >
      <VStack spacing={6} textAlign="center" maxW="400px">
        {/* Icon */}
        <Box
          w={16}
          h={16}
          borderRadius="full"
          bg={theme.colors.borderLight}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <WarningIcon boxSize={7} color={theme.colors.textMuted} />
        </Box>

        {/* Message */}
        <VStack spacing={3}>
          <Text
            as="h1"
            fontSize={{ base: '24px', md: '28px' }}
            fontWeight={600}
            color={theme.colors.textPrimary}
            fontFamily={theme.typography.fontFamily}
          >
            {title}
          </Text>
          <Text
            fontSize={theme.typography.body.fontSize}
            lineHeight={theme.typography.body.lineHeight}
            color={theme.colors.textSecondary}
            fontFamily={theme.typography.fontFamily}
          >
            {message}
          </Text>
        </VStack>

        {/* Retry button */}
        {showRetry && onRetry && (
          <Button
            onClick={onRetry}
            size="lg"
            bg={theme.colors.teal}
            color="white"
            height="52px"
            px={8}
            fontSize="16px"
            fontWeight={600}
            fontFamily={theme.typography.fontFamily}
            borderRadius={theme.radii.button}
            _hover={{
              bg: theme.colors.tealLight,
            }}
          >
            {t(lang, 'tryAgain')}
          </Button>
        )}
      </VStack>
    </Box>
  );
};

export default ErrorScreen;
